import {
  FORM_QUESTION_SOURCES,
  isPersonalInfoLibraryId,
} from "./publicJoinStorage";

const CONTEXT_FIELD_TYPES = new Set(["gym", "programme", "coach"]);
const NOTE_FIELD_TYPES = new Set([
  "long_text",
  "image_upload",
  "file_upload",
]);
const HIDDEN_PUBLIC_TYPES = new Set(["member", "status"]);

/**
 * Classify a composed tracker question into a guided-flow bucket.
 * Returns null when the question should not appear on the public form.
 */
export const classifyPublicQuestion = (q) => {
  if (!q) return null;
  if (String(q.mode || "").toLowerCase() === "coach_review") return null;
  if (HIDDEN_PUBLIC_TYPES.has(q.fieldType)) return null;

  if (
    q.source === FORM_QUESTION_SOURCES.PERSONAL_INFO ||
    isPersonalInfoLibraryId(q.questionId)
  ) {
    return "identity";
  }

  if (q.measurable === true || String(q.mode || "").toLowerCase() === "measurable") {
    return "measurable";
  }

  if (CONTEXT_FIELD_TYPES.has(q.fieldType)) return "context";
  if (NOTE_FIELD_TYPES.has(q.fieldType)) return "notes";

  return "experience";
};

export const filterPublicQuestions = (questions = []) =>
  (questions || []).filter((q) => classifyPublicQuestion(q) != null);

/** Questions a logged-in member should fill — personal info is taken from profile. */
export const filterMemberVisibleQuestions = (questions = []) =>
  filterPublicQuestions(questions).filter(
    (q) => classifyPublicQuestion(q) !== "identity"
  );

export const estimateMinutes = (questionCount = 0) =>
  Math.max(1, Math.ceil(Number(questionCount || 0) * 0.35));

/**
 * Adaptive guided steps from composed questions.
 * Order: Identity → Context → Measurable → Experience → Notes → Review?
 */
export const buildGuidedSteps = (
  questions = [],
  { isLead = false, forceReview = null } = {}
) => {
  const buckets = {
    identity: [],
    context: [],
    measurable: [],
    experience: [],
    notes: [],
  };

  (questions || []).forEach((q) => {
    const bucket = classifyPublicQuestion(q);
    if (bucket && buckets[bucket]) buckets[bucket].push(q);
  });

  const steps = [];

  if (buckets.identity.length) {
    steps.push({
      id: "identity",
      label: "Personal",
      short: "Who this is for",
      purpose:
        "Confirm these details so your gym can identify this submission.",
      questions: buckets.identity,
    });
  }

  if (buckets.context.length) {
    steps.push({
      id: "context",
      label: "Context",
      short: "Gym & programme",
      purpose: "Helps route you to the right coach or location.",
      questions: buckets.context,
    });
  }

  if (buckets.measurable.length) {
    steps.push({
      id: "measurable",
      label: "Measures",
      short: "Measurements",
      purpose: "Capture your latest numbers for this check-in.",
      questions: buckets.measurable,
    });
  }

  if (buckets.experience.length) {
    steps.push({
      id: "experience",
      label: isLead ? "Goals" : "Check-in",
      short: isLead ? "Your goals" : "How it’s going",
      purpose: isLead
        ? "Tell us what you’re looking for so we can follow up properly."
        : "Share habits and how you’ve been feeling — this helps your coach decide next steps.",
      questions: buckets.experience,
    });
  }

  if (buckets.notes.length) {
    steps.push({
      id: "notes",
      label: "Notes",
      short: "Anything else",
      purpose: "Optional context for your coach. Skip anything that doesn’t apply.",
      questions: buckets.notes,
    });
  }

  const answerableCount = filterPublicQuestions(questions).length;
  const shouldReview =
    forceReview != null
      ? forceReview
      : answerableCount >= 8 && steps.length >= 2;

  if (shouldReview) {
    steps.push({
      id: "review",
      label: "Review",
      short: "Review & submit",
      purpose: "Check your answers before sending them to the gym.",
      questions: [],
      isReview: true,
    });
  }

  return steps;
};

export const getLoggedInMemberPrefill = () => {
  if (!localStorage.getItem("token")) return null;
  try {
    const raw =
      localStorage.getItem("user_data_new") ||
      localStorage.getItem("user_data");
    if (!raw) return null;
    const member = JSON.parse(raw);
    if (!member) return null;
    return {
      firstName: String(member.first_name || "").trim(),
      lastName: String(member.last_name || "").trim(),
      email: String(member.email || "").trim(),
      phone: String(member.contact_number || member.phone || "").trim(),
      name: `${member.first_name || ""} ${member.last_name || ""}`.trim(),
    };
  } catch {
    return null;
  }
};

/** Build seed answers for identity fields from a logged-in member. */
export const buildIdentitySeedAnswers = (questions = [], member = null) => {
  if (!member) return {};
  const seed = {};
  (questions || []).forEach((q) => {
    if (classifyPublicQuestion(q) !== "identity") return;
    const lid = q.questionId;
    const label = String(q.label || "").toLowerCase();
    let value = "";
    if (lid === "q_first_name" || label.includes("first name")) {
      value = member.firstName;
    } else if (lid === "q_last_name" || label.includes("last name")) {
      value = member.lastName;
    } else if (lid === "q_email" || q.fieldType === "email") {
      value = member.email;
    } else if (lid === "q_phone" || q.fieldType === "phone") {
      value = member.phone;
    }
    if (value) seed[q.id] = value;
  });
  return seed;
};

export const formatAnswerForReview = (question, value) => {
  if (value == null || value === "") return "—";
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "—";
  }
  if (question?.fieldType === "yes_no") {
    if (value === "yes") return "Yes";
    if (value === "no") return "No";
  }
  return String(value);
};

export const getCoachDisplayName = (tracker) => {
  const coach = (tracker?.coaches || [])[0];
  if (!coach) return "";
  return (
    `${coach.first_name || ""} ${coach.last_name || ""}`.trim() ||
    coach.name ||
    coach.email ||
    ""
  );
};
